/**
 * Preloads recorded samples and hands out decoded buffers.
 *
 * ==========================================================================
 *  WHY IT IS SPLIT IN TWO
 * ==========================================================================
 *  Two clocks have to be respected at once, and they disagree:
 *
 *    * The bytes should be on the machine as early as possible - the moment
 *      the page opens, long before anybody clicks.
 *    * `decodeAudioData` needs an AudioContext, and a browser will not let one
 *      exist before a user gesture (see WebAudioManager).
 *
 *  So fetching and decoding are separate phases. `preload()` runs at import
 *  time and only downloads; `attach()` runs on the first click and turns
 *  whatever has arrived into AudioBuffers, plus anything still in flight as it
 *  lands. By the time the player has read the READY screen and pressed W, the
 *  buffers are decoded and a cue costs one `start()` call - no fetch, no
 *  decode, no first-play hitch on the frame it actually matters.
 *
 *  Nothing here can fail loudly. A sample that does not download, or does not
 *  decode, simply never appears in `get()`, and the caller falls back to the
 *  synthesised effect or the browser's own voice. The game is playable with an
 *  empty samples folder, so it must also be playable with a broken one.
 * ==========================================================================
 */

import type { SampleId, SampleSpec } from './samples';
import type { SampleStatus } from './AudioManager';

type FetchBytes = (url: string) => Promise<ArrayBuffer>;

export interface SampleBankOptions {
  /** Injectable for tests; defaults to `fetch`. */
  fetchBytes?: FetchBytes;
  /** Called once per file that never made it. Defaults to a console warning. */
  onProblem?: (url: string, error: unknown) => void;
}

interface Entry {
  spec: SampleSpec;
  bytes: Promise<ArrayBuffer> | null;
  /** Held so a file is decoded exactly once - decoding detaches its bytes. */
  decoding: Promise<void> | null;
  buffer: AudioBuffer | null;
  failed: boolean;
}

const defaultFetch: FetchBytes = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.arrayBuffer();
};

export class SampleBank {
  private readonly entries: Entry[];
  private readonly byId = new Map<SampleId, Entry[]>();
  private readonly fetchBytes: FetchBytes;
  private readonly onProblem: (url: string, error: unknown) => void;

  private ctx: AudioContext | null = null;
  private disposed = false;

  constructor(specs: readonly SampleSpec[], options: SampleBankOptions = {}) {
    this.fetchBytes = options.fetchBytes ?? defaultFetch;
    this.onProblem =
      options.onProblem ??
      ((url, error) => console.warn(`[laniaamu] sample unavailable: ${url}`, error));

    this.entries = specs.map((spec) => ({
      spec,
      bytes: null,
      decoding: null,
      buffer: null,
      failed: false,
    }));
    for (const entry of this.entries) {
      const takes = this.byId.get(entry.spec.id) ?? [];
      takes.push(entry);
      this.byId.set(entry.spec.id, takes);
    }
  }

  /** Starts every download. Repeat calls are free - each file is fetched once. */
  preload(): void {
    if (this.disposed) return;
    for (const entry of this.entries) this.fetchEntry(entry);
  }

  /**
   * Decodes everything into `ctx`. Resolves when the last file has either
   * decoded or given up, so a caller that wants to wait can - the game does
   * not, because a fallback exists for every cue.
   */
  async attach(ctx: AudioContext): Promise<void> {
    if (this.disposed) return;
    this.ctx = ctx;
    this.preload();
    await Promise.all(this.entries.map((entry) => this.decodeEntry(entry, ctx)));
  }

  /**
   * A decoded take of `id`, or null when there is nothing to play. `pick` is
   * the mixer's seeded PRNG: which take you get is random, but identically
   * random on every run of the same replay.
   */
  get(id: SampleId, pick: () => number): AudioBuffer | null {
    const takes = this.byId.get(id);
    if (!takes || takes.length === 0) return null;
    const ready = takes.filter((entry) => entry.buffer !== null);
    if (ready.length === 0) return null;
    const index = Math.min(ready.length - 1, Math.floor(pick() * ready.length));
    return ready[index].buffer;
  }

  /**
   * A take of `id` is still on its way: a file exists for it, and it has
   * neither decoded nor been given up on.
   *
   * The mixer asks this before falling back to a synthesised loop. A loop never
   * swaps its buffer once it is running - see `WebAudioManager.loopBuffer` -
   * so a loop that starts on the stand-in during the download is a loop that
   * never plays the recording at all.
   */
  pending(id: SampleId): boolean {
    const takes = this.byId.get(id);
    if (!takes) return false;
    return takes.some((entry) => entry.buffer === null && !entry.failed);
  }

  status(): SampleStatus {
    let ready = 0;
    let failed = 0;
    for (const entry of this.entries) {
      if (entry.buffer) ready++;
      else if (entry.failed) failed++;
    }
    return { total: this.entries.length, ready, failed };
  }

  dispose(): void {
    this.disposed = true;
    this.ctx = null;
    for (const entry of this.entries) {
      entry.bytes = null;
      entry.decoding = null;
      entry.buffer = null;
    }
  }

  /* --------------------------------------------------------------- internal */

  private fetchEntry(entry: Entry): void {
    if (entry.bytes || entry.failed) return;
    entry.bytes = this.fetchBytes(entry.spec.url);
    // A download that lands after attach() still has to be decoded, and the
    // context may not have existed when the fetch was started.
    void entry.bytes
      .then(() => {
        if (this.ctx) void this.decodeEntry(entry, this.ctx);
      })
      .catch((error: unknown) => this.fail(entry, error));
  }

  /**
   * At most one decode per file, ever. `attach()` and a download that lands
   * afterwards both aim at the same entry, and `decodeAudioData` detaches the
   * ArrayBuffer it is given - a second attempt would fail on empty bytes.
   */
  private decodeEntry(entry: Entry, ctx: AudioContext): Promise<void> {
    if (this.disposed || entry.buffer || entry.failed || !entry.bytes) return Promise.resolve();
    entry.decoding ??= this.decodeOnce(entry, ctx);
    return entry.decoding;
  }

  private async decodeOnce(entry: Entry, ctx: AudioContext): Promise<void> {
    try {
      const bytes = await entry.bytes;
      if (this.disposed || !bytes) return;
      const buffer = await ctx.decodeAudioData(bytes);
      if (this.disposed) return;
      entry.buffer = buffer;
    } catch (error) {
      this.fail(entry, error);
    }
  }

  private fail(entry: Entry, error: unknown): void {
    if (entry.failed || entry.buffer) return;
    entry.failed = true;
    entry.bytes = null;
    if (!this.disposed) this.onProblem(entry.spec.url, error);
  }
}
